import React from 'react';

import { Refresh } from '@mui/icons-material';
import { Alert, AlertTitle, Box, Container, IconButton, Stack } from '@mui/material';
import { compact, find, forEach, get, groupBy, isNil, some } from 'lodash';
import { LoadingSpinner, RadioButtonGroup } from '../../../components';
import { RestaurantSelector } from '../../../components/nav_bar/restaurant_selector';
import { AppContext } from '../../../contexts/app_context';
import { createScopedI18n, i18n } from '../../../i18n/i18n';
import { AttendancesPageContext } from './attendances_page_context';
import JobAttendancesList from './components/job_attendances_list';
import useQueryAttendances from './hooks/use_query_attendances';

import type { JobHiringAttendance } from './type';

const attendancesPageI18n = createScopedI18n('pages.attendances');

type AttendancesPageInnerProps = {
  setAttendances: React.Dispatch<React.SetStateAction<JobHiringAttendance[]>>;
  setCurrentAttendanceId: React.Dispatch<React.SetStateAction<string | null>>;
};

const AttendancesPageContent = React.memo(({ setAttendances, setCurrentAttendanceId }: AttendancesPageInnerProps) => {
  const { contextCurrentRestaurant } = React.useContext(AppContext);

  const {
    data: attendancesData,
    loading: attendancesLoading,
    refetch: refetchAttendances,
  } = useQueryAttendances({
    variables: { restaurantIds: compact([contextCurrentRestaurant?.id]) },
    skip: !contextCurrentRestaurant?.id,
    notifyOnNetworkStatusChange: true,
  });

  const attendances = React.useMemo(() => attendancesData?.jobHiringAttendances || [], [attendancesData]);

  React.useEffect(() => {
    setAttendances(attendances);
    setCurrentAttendanceId((prevId) => (find(attendances, ({ id }) => id === prevId) ? prevId : null));
  }, [attendances, setAttendances, setCurrentAttendanceId]);

  const refetchHandler = React.useCallback(() => {
    refetchAttendances();
  }, [refetchAttendances]);

  const attendancesByJob = React.useMemo(
    () => groupBy(attendances, (attendance) => get(attendance, 'jobHiring.job.id')),
    [attendances],
  );

  const [pendingAttendancesByJob, completedAttendancesByJob] = React.useMemo(() => {
    const pending: Record<string, typeof attendances> = {};
    const completed: Record<string, typeof attendances> = {};

    forEach(attendancesByJob, (jobAttendances, jobId) => {
      if (some(jobAttendances, (attendance) => attendance.status === 'pending')) {
        pending[jobId] = jobAttendances;
      } else {
        completed[jobId] = jobAttendances;
      }
    });

    return [pending, completed];
  }, [attendancesByJob]);

  const [tab, setTab] = React.useState<'active' | 'archived'>('active');

  if (isNil(contextCurrentRestaurant)) {
    return (
      <Container maxWidth="sm" sx={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Stack gap={1}>
          <Alert severity="warning">
            <AlertTitle>{i18n.t('errors.messages.restaurant_branch.blank')}</AlertTitle>
          </Alert>
          <RestaurantSelector
            variant="outlined"
            fullWidth
            sx={{ padding: 1 }}
            data-testid="restaurant-selector-warning"
          />
        </Stack>
      </Container>
    );
  }

  if (attendancesLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'auto' }}
    >
      <Box width="100%">
        <Stack padding={1} paddingTop={2} flexDirection="row" alignItems="center" gap={2}>
          <RadioButtonGroup
            color="primary"
            size="small"
            value={tab}
            onChangeValue={setTab}
            items={[
              { label: attendancesPageI18n('tabs.active'), value: 'active' },
              { label: attendancesPageI18n('tabs.archived'), value: 'archived' },
            ]}
          />
          <div style={{ flex: 1 }} />
          <IconButton onClick={refetchHandler}>
            <Refresh color="primary" />
          </IconButton>
        </Stack>
      </Box>

      <Box padding={1}>
        {tab === 'active' ? <JobAttendancesList attendancesByJob={pendingAttendancesByJob} /> : null}
        {tab === 'archived' ? <JobAttendancesList attendancesByJob={completedAttendancesByJob} /> : null}
      </Box>
    </Container>
  );
});

const AttendancesPage = () => {
  const { setAttendances, setCurrentAttendanceId } = React.useContext(AttendancesPageContext);
  return (
    <Box display="flex" flex={1} flexDirection="column" overflow="hidden">
      <AttendancesPageContent setAttendances={setAttendances} setCurrentAttendanceId={setCurrentAttendanceId} />
    </Box>
  );
};

export default AttendancesPage;
